import handleNumberFormat from '../../utils/handleNumberFormat';

export interface PizzaItemStat {
  name: string;
  quantity: number;
  total: number;
}

export type PizzaFlavorStat = PizzaItemStat;

export interface PizzaSizeStat {
  size: string;
  quantity: number;
  total: number;
  fromAtac: boolean;
  flavors: PizzaItemStat[];
  complements: PizzaItemStat[];
}

export interface PizzaSummary {
  sizes: PizzaSizeStat[];
  flavors: PizzaFlavorStat[];
}

export interface PizzaSourceItem {
  name?: string;
  quantity?: unknown;
  total?: unknown;
}

export interface PizzaSourceSummary {
  sizes?: Array<{
    size?: string;
    quantity?: unknown;
    total?: unknown;
    flavors?: PizzaSourceItem[];
    complements?: PizzaSourceItem[];
  }>;
  flavors?: PizzaSourceItem[];
}

export interface AtacPizzaDetailRow {
  nomeproduto?: string;
  quantity?: unknown;
  value?: unknown;
}

export interface AtacPizzaSizeDetails {
  status: 'loading' | 'ready' | 'error';
  flavors: PizzaItemStat[];
  complements: PizzaItemStat[];
}

export type PizzaSortKey = 'name' | 'quantity' | 'total';
export type PizzaSortDirection = 'asc' | 'desc';

export interface PizzaSort {
  key: PizzaSortKey;
  direction: PizzaSortDirection;
}

export function normalizePizzaKey(value: string): string {
  return value.trim().toLocaleLowerCase('pt-BR');
}

function asNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function mergePizzaItemStats(
  ...groups: Array<ReadonlyArray<PizzaSourceItem> | undefined | null>
): PizzaItemStat[] {
  const stats = new Map<string, PizzaItemStat>();

  groups.forEach((group) => {
    (group ?? []).forEach((row) => {
      const name = String(row.name ?? '').trim();
      if (!name) return;
      const key = normalizePizzaKey(name);
      const current = stats.get(key) ?? { name, quantity: 0, total: 0 };
      current.quantity += asNumber(row.quantity);
      current.total += asNumber(row.total);
      stats.set(key, current);
    });
  });

  return Array.from(stats.values());
}

export function atacRowsToPizzaStats(
  rows?: ReadonlyArray<AtacPizzaDetailRow> | null,
): PizzaItemStat[] {
  return mergePizzaItemStats(
    (rows ?? []).map((row) => {
      const quantity = asNumber(row.quantity);
      const value = asNumber(row.value);
      return {
        name: row.nomeproduto,
        quantity,
        total: quantity * value,
      };
    }),
  );
}

export function mergePizzaSummaries(
  atac?: PizzaSourceSummary | null,
  pdv?: PizzaSourceSummary | null,
): PizzaSummary {
  const sizes = new Map<string, PizzaSizeStat>();
  const flavors = new Map<string, PizzaFlavorStat>();

  const addSize = (
    row: {
      size?: string;
      quantity?: unknown;
      total?: unknown;
      flavors?: PizzaSourceItem[];
      complements?: PizzaSourceItem[];
    },
    fromAtac: boolean,
  ) => {
    const size = String(row.size ?? '').trim();
    if (!size) return;
    const key = normalizePizzaKey(size);
    const current = sizes.get(key) ?? {
      size,
      quantity: 0,
      total: 0,
      fromAtac: false,
      flavors: [],
      complements: [],
    };
    current.quantity += asNumber(row.quantity);
    current.total += asNumber(row.total);
    current.fromAtac = current.fromAtac || fromAtac;
    current.flavors = mergePizzaItemStats(current.flavors, row.flavors);
    current.complements = mergePizzaItemStats(
      current.complements,
      row.complements,
    );
    sizes.set(key, current);
  };

  const addFlavor = (row: PizzaSourceItem) => {
    const name = String(row.name ?? '').trim();
    if (!name) return;
    const key = normalizePizzaKey(name);
    const current = flavors.get(key) ?? { name, quantity: 0, total: 0 };
    current.quantity += asNumber(row.quantity);
    current.total += asNumber(row.total);
    flavors.set(key, current);
  };

  (atac?.sizes ?? []).forEach((row) => addSize(row, true));
  (pdv?.sizes ?? []).forEach((row) => addSize(row, false));
  (atac?.flavors ?? []).forEach(addFlavor);
  (pdv?.flavors ?? []).forEach(addFlavor);

  return {
    sizes: Array.from(sizes.values()).sort((a, b) => b.quantity - a.quantity),
    flavors: Array.from(flavors.values()).sort((a, b) => b.quantity - a.quantity),
  };
}

export function resolvePizzaSizeDetails(
  size: PizzaSizeStat,
  atac?: AtacPizzaSizeDetails,
): {
  loading: boolean;
  flavors: PizzaItemStat[];
  complements: PizzaItemStat[];
} {
  if (atac?.status === 'loading') {
    return { loading: true, flavors: [], complements: [] };
  }

  const atacReady = atac?.status === 'ready' ? atac : undefined;

  return {
    loading: false,
    flavors: mergePizzaItemStats(size.flavors, atacReady?.flavors),
    complements: mergePizzaItemStats(size.complements, atacReady?.complements),
  };
}

export function pizzaGroupTotals(summary: PizzaSummary): {
  quantity: number;
  total: number;
} {
  return summary.sizes.reduce(
    (totals, size) => ({
      quantity: totals.quantity + size.quantity,
      total: totals.total + size.total,
    }),
    { quantity: 0, total: 0 },
  );
}

export function formatPizzaSize(size: string): string {
  const label = size.trim().toLocaleLowerCase('pt-BR');
  return label ? `Pizza ${label}` : 'Pizza';
}

export function formatPizzaQuantity(quantity: number): string {
  if (Number.isInteger(quantity)) {
    return handleNumberFormat(quantity);
  }

  return quantity.toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  });
}

export function sortPizzaStats(
  items: PizzaItemStat[],
  key: PizzaSortKey,
  direction: PizzaSortDirection,
): PizzaItemStat[] {
  const factor = direction === 'asc' ? 1 : -1;

  return [...items].sort((a, b) => {
    if (key === 'name') {
      return (
        factor *
        a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
      );
    }

    const diff = a[key] - b[key];
    if (diff !== 0) return factor * diff;
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
  });
}

export function togglePizzaSort(current: PizzaSort, key: PizzaSortKey): PizzaSort {
  if (current.key === key) {
    return {
      key,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
  }

  return {
    key,
    direction: key === 'name' ? 'asc' : 'desc',
  };
}
